/**
 * @name Pagination.js
 * @author 김권영
 * @since 2025. 01. 06.
 * @description
 * 공통 페이징 네비게이션
 * 
 * @param {number} totalPages - 총 페이지 개수
 * @param {number} currentPage - 현재 페이지
 * @param {function} onPageChange - 페이지 이동 함수
 * @param {number} pageRangeDisplayed - 보여줄 페이지 수
 */
import React from 'react';
import PropTypes from 'prop-types';
import './Pagination.css';
const Pagination = ({
    totalPages,
    currentPage,
    onPageChange,
    pageRangeDisplayed = 5,
}) => {
    if (totalPages === 0) return null; 

    const pageNumbers = [];
    const halfRange = Math.floor(pageRangeDisplayed / 2);
    let startPage = currentPage - halfRange;
    let endPage = currentPage + halfRange;

    if (startPage < 1) {
        startPage = 1;
        endPage = Math.min(totalPages, pageRangeDisplayed);
    }
    if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, totalPages - pageRangeDisplayed + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className="pagination">
            <button
                type="button"
                className="page-num"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                &lt;
            </button>
            {startPage > 1 && (
                <>
                    <button type="button" className="page-num" onClick={() => onPageChange(1)}>
                        1
                    </button>
                    {startPage > 2 && <span className="ellipsis">...</span>}
                </>
            )}
            {pageNumbers.map((number) => (
                <button
                    type="button"
                    key={number}
                    className={`page-num ${currentPage === number ? 'active' : ''}`}
                    onClick={() => onPageChange(number)}
                >
                    {number}
                </button>
            ))}
            {endPage < totalPages && (
                <>
                    {endPage < totalPages - 1 && <span className="ellipsis">...</span>}
                    <button type="button" className="page-num" onClick={() => onPageChange(totalPages)}>
                        {totalPages}
                    </button>
                </>
            )}
            <button
                type="button"
                className="page-num"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                &gt;
            </button>
        </div>
    );
};

Pagination.propTypes = {
    totalPages: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    pageRangeDisplayed: PropTypes.number,
};

export default Pagination;
